import React, { useState } from 'react';
import { useDashboardData } from '../hooks/useDashboardData';
import { useBookings } from '../hooks/useBookings';
import { Building2, Users, Calendar, DollarSign, Eye, BookOpen } from 'lucide-react';
import ViewBookingModal from '../components/ViewBookingModal';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';
import useDebounce from '../hooks/useDebounce';

const Dashboard = () => {
  const { data: dashboardData } = useDashboardData();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const debouncedSearchInput = useDebounce(searchInput, 500);

  const filters = {};
  if (debouncedSearchInput) filters.search = debouncedSearchInput;
  if (statusFilter) filters.status = statusFilter;

  const { data: bookingsData, isLoading: isLoadingBookings } = useBookings(currentPage, filters);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const bookings = bookingsData?.bookings || [];
  const pagination = bookingsData?.pagination;

  const getNights = (checkInDate, checkOutDate) => {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    return Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  };



  const totalProperties = dashboardData?.allProperties || 0;
  const totalBookings = dashboardData?.totalBookings || 0;
  const totalCustomers = dashboardData?.allCustomers || 0;
  const totalRevenue = dashboardData?.totalRevenue || 0;

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6 hover:border-prose-gold transition duration-200">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-prose-black/60 text-xs sm:text-sm font-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-prose-brand mt-2 truncate">{value}</p>
        </div>
        <div className={`p-2 sm:p-3 rounded-lg border ${color} flex-shrink-0`}>
          <Icon size={24} className="sm:w-7 sm:h-7" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-prose-brand">Dashboard</h1>
        <p className="text-xs sm:text-sm text-prose-black/60 mt-1">Welcome back! Here's your property overview.</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          label="All Properties"
          value={totalProperties}
          color="border-blue-300 text-blue-600"
        />
        <StatCard
          icon={Calendar}
          label="Total Bookings"
          value={totalBookings}
          color="border-green-300 text-green-600"
        />
        <StatCard
          icon={Users}
          label="All Customers"
          value={totalCustomers}
          color="border-purple-300 text-purple-600"
        />
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={`£${parseFloat(totalRevenue).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
          color="border-prose-gold text-prose-gold-dark"
        />
      </div>

      {/* Bookings Table Section */}
      {isLoadingBookings ? (
        <LoadingSpinner message="Loading bookings..." />
      ) : (
      <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-300">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Bookings</h2>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {/* Search */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">Search by Customer/Property</label>
              <input
                type="text"
                placeholder="e.g., John, Lagos Apartment..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand/50 text-xs sm:text-sm"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand/50 text-xs sm:text-sm"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(searchInput || statusFilter) && (
            <button
              onClick={() => {
                setSearchInput('');
                setStatusFilter('');
                setCurrentPage(1);
              }}
              className="mt-4 px-4 py-2 text-sm font-medium text-prose-brand border border-prose-brand hover:bg-prose-brand hover:text-white rounded-lg transition duration-200"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">
                  Customer
                </th>
                <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">
                  Property
                </th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">
                  Check-in
                </th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">
                  Check-out
                </th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">
                  Nights
                </th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Amount</th>
                <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Status</th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Payment Txn Ref</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-center font-semibold text-prose-brand">Action</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length > 0 ? (
                bookings.map((booking) => {
                  const nights = getNights(booking.check_in_date, booking.check_out_date);
                  const totalPrice = parseFloat(booking.total_price);
                  return (
                    <tr
                      key={booking.id}
                      onClick={() => {
                        setSelectedBooking(booking);
                        setIsViewModalOpen(true);
                      }}
                      className="border-b border-gray-300/60 hover:bg-gray-50 transition duration-200 cursor-pointer"
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-4 font-medium text-prose-black text-xs sm:text-sm">
                        {booking.customer?.name || 'N/A'}
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs sm:text-sm">{booking.property?.title || 'N/A'}</td>
                      <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs">
                        {new Date(booking.check_in_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs">
                        {new Date(booking.check_out_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black font-medium text-xs sm:text-sm">
                        {nights} night{nights !== 1 ? 's' : ''}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 font-semibold text-prose-gold-dark text-xs sm:text-sm">
                        £{totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs">
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {booking.status || 'N/A'}
                        </span>
                      </td>
                      <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs sm:text-sm">
                        {booking.payment_tnx_ref || 'N/A'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBooking(booking);
                            setIsViewModalOpen(true);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition duration-200 text-prose-brand"
                          title="View Details"
                        >
                          <Eye size={16} className="sm:w-4.5 sm:h-4.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="px-3 sm:px-6 py-12 sm:py-16">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="p-3 sm:p-4 rounded-full bg-gray-100">
                        <BookOpen size={32} className="text-prose-black/40" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm sm:text-base font-semibold text-prose-black/70">No bookings found</p>
                        <p className="text-xs sm:text-sm text-prose-black/50 mt-1">
                          {statusFilter || searchInput
                            ? 'Try adjusting your filters'
                            : 'Bookings will appear here once customers start booking'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && bookings.length > 0 && (
          <div className="p-4 sm:p-6 border-t border-gray-300">
            <Pagination
              pagination={pagination}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
      )}

      {/* View Booking Modal */}
      <ViewBookingModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedBooking(null);
        }}
        booking={selectedBooking}
      />
    </div>
  );
};

export default Dashboard;
